import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PiCalendarDuotone,
  PiCheckCircleDuotone,
  PiMagnifyingGlassDuotone,
  PiSwordDuotone,
} from 'react-icons/pi';
import { getEvents } from '../api/eventService';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import EmptyState from '../shared/common/EmptyState';
import { SkeletonGrid } from '../shared/common/Skeleton';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';
import Select from '../shared/common/Select';
import Input from '../shared/common/Input';
import DateTimePicker from '../shared/common/DateTimePicker';
import { EVENT_CATEGORY_OPTIONS, getEventCategoryLabel } from '../models/eventCategories';
import { buildRoomSelectionPathForEvent } from '../shared/utils/challengeCreationFlow';
import { toUserOffsetDateTime } from '../shared/utils/dateTime';

const DEFAULT_PERIOD = 'CURRENT_AND_FUTURE';
const PERIOD_CUSTOM = 'CUSTOM';

const DEFAULT_FILTERS = Object.freeze({
  query: '',
  dateFrom: '',
  dateTo: '',
  period: DEFAULT_PERIOD,
  ownership: 'ALL',
  resultStatus: 'ALL',
  archiveStatus: 'ACTIVE',
  category: '',
});
const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 12;
const FIRST_PAGE_LABEL = 1;
const FILTER_DEBOUNCE_MS = 300;

const PERIOD_OPTIONS = [
  { value: DEFAULT_PERIOD, label: 'Текущие и будущие' },
  { value: PERIOD_CUSTOM, label: 'Любой период' },
  { value: 'TODAY', label: 'Сегодня' },
  { value: 'NEXT_7_DAYS', label: 'Ближайшие 7 дней' },
  { value: 'NEXT_MONTH', label: 'Ближайший месяц' },
  { value: 'PAST', label: 'Прошедшие' },
];

const OWNERSHIP_OPTIONS = [
  { value: 'ALL', label: 'Все события' },
  { value: 'OWN', label: 'Мои события' },
  { value: 'USER_CREATED', label: 'Чужие пользовательские' },
  { value: 'GLOBAL', label: 'Глобальные' },
];

const RESULT_STATUS_OPTIONS = [
  { value: 'ALL', label: 'Все результаты' },
  { value: 'PENDING', label: 'Ожидает результата' },
  { value: 'SPECIFIED', label: 'Результат указан' },
];

const ARCHIVE_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Активные' },
  { value: 'ARCHIVED', label: 'Архивированные' },
  { value: 'ALL', label: 'Все' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'Все категории' },
  ...EVENT_CATEGORY_OPTIONS,
];

const formatDate = (dateStr) => {
  if (!dateStr) return null;

  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatEventDate = (event) => {
  const start = formatDate(event?.startDate || event?.eventDate || event?.date);
  const end = formatDate(event?.endDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

const hasResult = (event) => Boolean(String(event?.result || '').trim());

const isFutureEvent = (event) => {
  const startDate = event?.startDate || event?.eventDate || event?.date;
  if (!startDate) return false;
  return new Date(startDate).getTime() > Date.now();
};

const isDefaultFilters = (filters) => Object.keys(DEFAULT_FILTERS)
  .every((key) => filters[key] === DEFAULT_FILTERS[key]);

const resolveResultState = (event) => {
  if (hasResult(event)) {
    return {
      badgeStatus: 'resolved',
      badgeText: 'результат указан',
      hint: `Результат: ${event.result}`,
      actionText: null,
      actionIcon: null,
    };
  }

  if (event?.editable) {
    return {
      badgeStatus: 'closed',
      badgeText: 'нужен результат',
hint: 'Укажи итог, чтобы завершить связанные пари',
      actionText: 'Указать результат',
      actionIcon: <PiCheckCircleDuotone size={14} />,
    };
  }

  return {
    badgeStatus: 'open',
    badgeText: 'ожидает результата',
hint: 'Пари завершатся после фиксации официального результата',
    actionText: null,
    actionIcon: null,
  };
};

const buildSearchParams = (filters, page) => ({
  query: filters.query,
  dateFrom: filters.period === PERIOD_CUSTOM ? toUserOffsetDateTime(filters.dateFrom) : null,
  dateTo: filters.period === PERIOD_CUSTOM ? toUserOffsetDateTime(filters.dateTo) : null,
  period: filters.period,
  ownership: filters.ownership,
  resultStatus: filters.resultStatus,
  archiveStatus: filters.archiveStatus,
  category: filters.category || null,
  page,
  size: DEFAULT_PAGE_SIZE,
});

export default function EventListPage() {
  const [events, setEvents] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalElements: 0,
  });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const activeFilters = useMemo(() => !isDefaultFilters(filters), [filters]);

  useEffect(() => {
    document.title = 'BetMate — События';
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(true);
      setError('');
      getEvents(buildSearchParams(filters, page))
        .then((data) => {
          const content = Array.isArray(data?.content) ? data.content : [];
          setEvents(content);
          setPageInfo({
            page: Number(data?.page ?? page),
            size: Number(data?.size ?? DEFAULT_PAGE_SIZE),
            totalPages: Number(data?.totalPages ?? 0),
            totalElements: Number(data?.totalElements ?? content.length),
          });
        })
        .catch(() => {
          setEvents([]);
          setPageInfo({ page, size: DEFAULT_PAGE_SIZE, totalPages: 0, totalElements: 0 });
          setError('Не удалось загрузить события');
        })
        .finally(() => setLoading(false));
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [filters, page]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(DEFAULT_PAGE);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(DEFAULT_PAGE);
  };

  const handleCreateChallengeFromEvent = (event) => {
    if (!event?.id) return;
    navigate(buildRoomSelectionPathForEvent(event.id));
  };

  const visibleEvents = events.filter((ev) => ev?.id && ev?.title);
  const hasNextPage = pageInfo.totalPages > pageInfo.page + FIRST_PAGE_LABEL;
  const hasPreviousPage = pageInfo.page > DEFAULT_PAGE;

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="bm-h2">События</h1>
          <p className="bm-text-secondary bm-text-sm" style={{ marginTop: 4 }}>
            Все доступные события для ставок
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            icon={<PiMagnifyingGlassDuotone size={16} />}
            onClick={() => navigate('/events/external')}
          >
            Найти футбольные события
          </Button>
          <Button
            variant="primary"
            icon={<PiCalendarDuotone size={16} />}
            onClick={() => navigate('/events/create')}
          >
            Создать событие
          </Button>
        </div>
      </div>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <Input
            label="Поиск"
            placeholder="Название события"
            value={filters.query}
            icon={<PiMagnifyingGlassDuotone size={16} />}
            onChange={(event) => handleFilterChange('query', event.target.value)}
          />
          <Select
            label="Период"
            value={filters.period}
            options={PERIOD_OPTIONS}
            onChange={(value) => handleFilterChange('period', value)}
          />
          <Select
            label="Принадлежность"
            value={filters.ownership}
            options={OWNERSHIP_OPTIONS}
            onChange={(value) => handleFilterChange('ownership', value)}
          />
          <Select
            label="Результат"
            value={filters.resultStatus}
            options={RESULT_STATUS_OPTIONS}
            onChange={(value) => handleFilterChange('resultStatus', value)}
          />
          <Select
            label="Статус"
            value={filters.archiveStatus}
            options={ARCHIVE_STATUS_OPTIONS}
            onChange={(value) => handleFilterChange('archiveStatus', value)}
          />
          <Select
            label="Категория"
            value={filters.category}
            options={CATEGORY_FILTER_OPTIONS}
            onChange={(value) => handleFilterChange('category', value)}
          />
          <DateTimePicker
            label="Дата с"
            value={filters.dateFrom}
            disabled={filters.period !== PERIOD_CUSTOM}
            onChange={(v) => handleFilterChange('dateFrom', v)}
          />
          <DateTimePicker
            label="Дата по"
            value={filters.dateTo}
            disabled={filters.period !== PERIOD_CUSTOM}
            onChange={(v) => handleFilterChange('dateTo', v)}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <p className="bm-text-secondary bm-text-sm">
            Найдено: {pageInfo.totalElements}. Фильтры {activeFilters ? 'применены' : 'не выбраны'}.
          </p>
          <Button variant="ghost" size="sm" onClick={resetFilters} disabled={!activeFilters}>
            Сбросить фильтры
          </Button>
        </div>
      </Card>

      {error && <div className="bm-auth-error">{error}</div>}

      {showSkeleton ? (
        <SkeletonGrid count={6} />
      ) : visibleEvents.length === 0 ? (
        <EmptyState
          icon={<PiCalendarDuotone size={40} />}
          title={activeFilters ? 'События не найдены' : 'Событий пока нет'}
          hint={activeFilters ? 'Попробуй изменить запрос или сбросить фильтры' : 'Создай первое событие вручную или найди матч через футбольный API'}
          actionLabel={activeFilters ? 'Сбросить фильтры' : 'Найти футбольные события'}
          onAction={activeFilters ? resetFilters : () => navigate('/events/external')}
        />
      ) : (
        <>
          <div className="bm-grid">
            {visibleEvents.map((ev) => {
              const eventDate = formatEventDate(ev);
              const resultState = resolveResultState(ev);
              const categoryLabel = getEventCategoryLabel(ev);
              const canCreateChallenge = isFutureEvent(ev);

              return (
                <Card
                  key={ev.id}
                  variant="interactive"
                  onClick={() => navigate(`/events/${ev.id}`)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div
                      style={{
                        width: 40, height: 40, borderRadius: 'var(--bm-radius-sm)',
                        background: 'rgba(0,214,143,0.1)', color: 'var(--bm-accent-green)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >
                      <PiCalendarDuotone size={20} />
                    </div>
                    <Badge status={resultState.badgeStatus} pulse={resultState.badgeStatus === 'open'}>
                      {resultState.badgeText}
                    </Badge>
                    <Badge status="primary">{categoryLabel}</Badge>
                  </div>
                  <h4 className="bm-h4">{ev.title}</h4>
                  <p className="bm-text-secondary bm-text-sm">
                    {eventDate ? `Период: ${eventDate}` : 'Даты не указаны'}
                  </p>
                  <p className="bm-text-secondary bm-text-sm">
                    {resultState.hint}
                  </p>
                  <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--bm-border)', display: 'grid', gap: 8 }}>
                    {resultState.actionText && (
                      <Button
                        variant="success"
                        size="sm"
                        icon={resultState.actionIcon}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/events/${ev.id}`);
                        }}
                        style={{ width: '100%' }}
                      >
                        {resultState.actionText}
                      </Button>
                    )}
                    {canCreateChallenge && (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<PiSwordDuotone size={14} />}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCreateChallengeFromEvent(ev);
                        }}
                        style={{ width: '100%' }}
                      >
Создать пари
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasPreviousPage}
              onClick={() => setPage((currentPage) => Math.max(DEFAULT_PAGE, currentPage - FIRST_PAGE_LABEL))}
            >
              Назад
            </Button>
            <span className="bm-text-secondary bm-text-sm">
              Страница {pageInfo.page + FIRST_PAGE_LABEL} из {Math.max(pageInfo.totalPages, FIRST_PAGE_LABEL)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasNextPage}
              onClick={() => setPage((currentPage) => currentPage + FIRST_PAGE_LABEL)}
            >
              Дальше
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
